/**
 * Design tokens for the "Console" visual system.
 * Source of truth: design_handoff_console_revamp/README.md § Design Tokens.
 *
 * CommonJS on purpose: tailwind.config.js runs in Node and `require()`s this file,
 * while webpack still lets app code `import tokens from './tokens'`. One copy, no drift.
 */

const color = {
  bg: "#FBFBFC",
  surface: "#FFFFFF",
  dark: "#17171F",

  border: "#E8E8EC",
  borderInput: "#E0E0E6",
  borderRow: "#F0F0F3",

  text: "#1E1E24",
  textSecondary: "#55555E",
  textMuted: "#8E8E99",

  indigo: "#4C4CDD",
  indigoHover: "#3A3AB8",
  indigoTint: "#EEEEFC",
  rowTint: "#F7F7FF",

  success: "#1D8F4E",
  successBg: "#E7F5EC",
  warning: "#A1720B",
  warningBg: "#FFF4DD",
  danger: "#C23934",
  dangerBg: "#FDECEA",
  purple: "#7A3FD2",
  purpleBg: "#F0E4FF",

  // Score bands. Deliberately separate from the semantic colours above: a score is a
  // measurement, not a status, and the two shouldn't drift together. Blue is pulled from
  // the avatar palette so it stays in-system, and is kept distinct enough from the indigo
  // accent that a good score doesn't read as a selected row.
  scoreGreen: "#1D8F4E",
  scoreBlue: "#2A5AE8",
  scoreOrange: "#C2680C",
  scoreRedLight: "#D2544A",
  scoreRedDark: "#8E1B15",
};

/** Cycled by index to tint candidate avatars. */
const avatarPalette = [
  { bg: "#DBE6FF", fg: "#2A5AE8" },
  { bg: "#FFE4D9", fg: "#D2492F" },
  { bg: "#E0F2E6", fg: "#1D7A3F" },
  { bg: "#F0E4FF", fg: "#7A3FD2" },
  { bg: "#FFF1CF", fg: "#A1720B" },
];

const font = {
  sans: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  // All numbers, scores, IDs, kbd hints, timestamps.
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

const radius = {
  chip: 4,
  control: 7,
  button: 6,
  card: 9,
  fileRow: 8,
  modal: 12,
  pill: 14,
};

const shadow = {
  card: "0 1px 3px rgba(23,23,31,.05)",
  modal: "0 20px 60px rgba(23,23,31,.3)",
  panel: "-16px 0 40px rgba(23,23,31,.12)",
};

const layout = {
  topBar: 48,
  rail: 264,
  // Wide enough for a full transcript line and the screening record without the panel turning
  // into a column of two-word wraps. Matches createPanel so the two side panels agree.
  detailPanel: 520,
  createPanel: 520,
  rowHeight: 40,
  touchRowHeight: 44,
};

/**
 * Colour for a score on the 0–10 scale.
 *
 *   >= 8   green        strong
 *   6 – 8  blue         solid
 *   5 – 6  orange       borderline
 *   2 – 5  light red    weak
 *   < 2    dark red     very weak
 *
 * Bands are inclusive at the lower bound, so exactly 8.0 is green and exactly 6.0 is blue.
 * An absent score is muted grey rather than dark red — "not scored" is not "scored zero",
 * and colouring it red would libel a candidate nobody has assessed yet.
 */
const scoreColor = (score) => {
  if (score === null || score === undefined || score === "") return color.textMuted;
  const n = parseFloat(score);
  if (Number.isNaN(n)) return color.textMuted;

  if (n >= 8) return color.scoreGreen;
  if (n >= 6) return color.scoreBlue;
  if (n >= 5) return color.scoreOrange;
  if (n >= 2) return color.scoreRedLight;
  return color.scoreRedDark;
};

module.exports = {
  color,
  avatarPalette,
  font,
  radius,
  shadow,
  layout,
  scoreColor,
};
